import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Skeleton, Stack } from '@mui/material';
import { billingErrorMessage } from './errorMessage.js';
import { scheduleBillingQueryInvalidations } from '../../hooks/api/billingReconciliationQueryInvalidation.js';
import {
  ALL,
  EXPORT_PREVIEW_DEFAULT_PAGE_SIZE,
  laneCanExport,
} from './exportPreviewHelpers.js';
import {
  ExportBlockersAlert,
  ExportHistoryPanel,
  ExportPreviewTable,
  ExportPreviewToolbar,
} from './ExportPreviewSections.jsx';
import {
  downloadBillingReconciliationExportBundleFile,
  downloadBillingReconciliationExportLineageCsvFile,
  downloadBillingReconciliationExportFile,
  downloadBillingReconciliationActionSummaryCsvFile,
  useBillingReconciliationExports,
  useBillingReconciliationExportPreview,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const ExportPreviewPanel = ({ runId, onOpenBac = null }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(EXPORT_PREVIEW_DEFAULT_PAGE_SIZE);
  const [binding, setBinding] = useState(ALL);
  const [exportError, setExportError] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const previewQuery = useBillingReconciliationExportPreview({
    runId,
    binding: binding === ALL ? null : binding,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  }, { enabled: Boolean(runId) });
  const exportsQuery = useBillingReconciliationExports({ runId, limit: 6 }, { enabled: Boolean(runId) });
  const rows = useMemo(() => previewQuery.data?.rows || [], [previewQuery.data?.rows]);
  const summary = previewQuery.data?.summary || { byLane: {}, byMutation: {}, draftCount: 0, total: 0 };
  const rowsMeta = previewQuery.data?.rowsMeta || {};
  const cycleClose = previewQuery.data?.cycleClose || {};
  const readiness = previewQuery.data?.readiness || { lanes: {}, blockers: [], warnings: [] };
  const rowCount = Number.isFinite(Number(rowsMeta.filteredTotal)) ? Number(rowsMeta.filteredTotal) : rows.length;
  const lastValidPage = Math.max(0, Math.ceil(rowCount / rowsPerPage) - 1);
  const laneCounts = summary.byLane || {};
  const gmLane = readiness.lanes?.gm_correction || {};
  const cpqLane = readiness.lanes?.cpq_peoplesoft_adjustment || {};
  const blockers = readiness.blockers || [];
  const canExportGm = laneCanExport(Number(laneCounts.gm_correction || 0) > 0, gmLane);
  const canExportCpq = laneCanExport(Number(laneCounts.cpq_peoplesoft_adjustment || 0) > 0, cpqLane);
  const canDownloadActionCsv = Number(summary.total || 0) > 0 && readiness.status === 'ready';
  const deltaValue = Number(cycleClose.delta || 0);

  useEffect(() => {
    setPage(0);
  }, [runId]);

  useEffect(() => {
    if (page > lastValidPage) setPage(lastValidPage);
  }, [lastValidPage, page]);

  if (!runId) {
    return <Alert severity="info">Create or select a round before exporting billing adjustments.</Alert>;
  }
  if (previewQuery.isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton height={48} />
        <Skeleton height={160} />
      </Stack>
    );
  }
  if (previewQuery.isError) {
    return <Alert severity="error">{billingErrorMessage(previewQuery.error, 'Export preview could not be loaded.')}</Alert>;
  }

  const refreshExportState = () => {
    [
      ['billingRecon', 'exports', runId],
      ['billingRecon', 'exportPreview', runId],
      ['billingRecon', 'actionSummary', runId],
      ['billingRecon', 'varianceActions'],
      ['billingRecon', 'variancesAll', runId],
      ['billingRecon', 'variances', runId],
    ].forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
    });
    scheduleBillingQueryInvalidations(queryClient, [
      ['billingRecon', 'exports', runId],
      ['billingRecon', 'exportPreview', runId],
      ['billingRecon', 'actionSummary', runId],
      ['billingRecon', 'varianceActions'],
    ], { delayMs: 5_000, refetchType: 'active' });
    scheduleBillingQueryInvalidations(queryClient, [
      ['billingRecon', 'exports', runId],
      ['billingRecon', 'exportPreview', runId],
      ['billingRecon', 'actionSummary', runId],
      ['billingRecon', 'varianceActions'],
    ], { delayMs: 20_000, refetchType: 'active' });
  };
  const handleExport = async (lane) => {
    try {
      setExportError('');
      setExportMessage('');
      await downloadBillingReconciliationExportFile({ runId, lane });
      refreshExportState();
      setExportMessage('Export generated. Export history will refresh in the background.');
    } catch (error) {
      setExportError(billingErrorMessage(error, 'Export could not be generated.'));
    }
  };
  const handleActionCsv = async () => {
    try {
      setExportError('');
      setExportMessage('');
      await downloadBillingReconciliationActionSummaryCsvFile({ runId });
    } catch (error) {
      setExportError(billingErrorMessage(error, 'Action summary CSV could not be generated.'));
    }
  };
  const handleLineageCsv = async () => {
    try {
      setExportError('');
      setExportMessage('');
      await downloadBillingReconciliationExportLineageCsvFile({ runId });
    } catch (error) {
      setExportError(billingErrorMessage(error, 'Lineage CSV could not be generated.'));
    }
  };
  const handleBundle = async () => {
    try {
      setExportError('');
      setExportMessage('');
      await downloadBillingReconciliationExportBundleFile({ runId });
      refreshExportState();
      setExportMessage('Export bundle generated. Export history will refresh in the background.');
    } catch (error) {
      setExportError(billingErrorMessage(error, 'Export bundle could not be generated.'));
    }
  };

  return (
    <Stack spacing={2}>
      {summary.draftCount > 0 && (
        <Alert severity="warning">
          {summary.draftCount} draft action{summary.draftCount === 1 ? '' : 's'} will not be exported. Save changes before generating files.
        </Alert>
      )}
      {exportError && (
        <Alert severity="error" onClose={() => setExportError('')}>
          {exportError}
        </Alert>
      )}
      {exportMessage && (
        <Alert severity="success" onClose={() => setExportMessage('')}>
          {exportMessage}
        </Alert>
      )}
      <ExportPreviewToolbar
        binding={binding}
        blockers={blockers}
        canDownloadActionCsv={canDownloadActionCsv}
        canExportCpq={canExportCpq}
        canExportGm={canExportGm}
        cycleClose={cycleClose}
        deltaValue={deltaValue}
        draftCount={summary.draftCount}
        exportsQuery={exportsQuery}
        handleActionCsv={handleActionCsv}
        handleBundle={handleBundle}
        handleExport={handleExport}
        handleLineageCsv={handleLineageCsv}
        readiness={readiness}
        setBinding={setBinding}
        setPage={setPage}
        summary={summary}
      />
      <ExportBlockersAlert blockers={blockers} onOpenBac={onOpenBac} />
      <ExportHistoryPanel exportsQuery={exportsQuery} runId={runId} />
      <ExportPreviewTable
        lastValidPage={lastValidPage}
        onOpenBac={onOpenBac}
        page={page}
        rowCount={rowCount}
        rows={rows}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
      />
    </Stack>
  );
};

export default ExportPreviewPanel;
